import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Project } from '../types';
import { customersAPI } from '../services/api';
import { formatDate } from '../utils/dateFormat';

interface ProjectsTableProps {
  projects: Project[];
  statuses: { id: number; title: string }[];
  loadingStatuses: boolean;
  statusFilter: number;
  customerOptions: { value: number; label: string }[];
  customerFilter: number;
  searchText: string;
  onlyWithoutTransactions: boolean;
  onSearchTextChange: (newValue: string) => void;
  onStatusFilterChange: (newValue: number) => void;
  onCustomerFilterChange: (newValue: number) => void;
  onOnlyWithoutTransactionsChange: (newValue: boolean) => void;
  onEdit: (project: Project) => void;
  onDelete: (id: number) => void;
  onViewDetails: (id: number) => void;
  /**
   * Supplying this handler renders the per-row "Create Transaction" action.
   * Omit it to hide transaction management for a read-only view (for example Leads).
   */
  onCreateTransaction?: (projectId: number) => void;
}

const ProjectsTable: React.FC<ProjectsTableProps> = ({
  projects,
  statuses,
  loadingStatuses,
  statusFilter,
  customerOptions,
  customerFilter,
  searchText,
  onlyWithoutTransactions,
  onSearchTextChange,
  onStatusFilterChange,
  onCustomerFilterChange,
  onOnlyWithoutTransactionsChange,
  onEdit,
  onDelete,
  onViewDetails,
  onCreateTransaction
}) => {
  const [customerGroupMap, setCustomerGroupMap] = useState<Record<number, string>>({});

  useEffect(() => {
    loadCustomerGroups();
  }, [projects]);

  const loadCustomerGroups = async () => {
    try {
      const customers = await customersAPI.getCustomers();
      const map = customers.reduce<Record<number, string>>((accumulator, customer) => {
        if (customer.group) {
          accumulator[customer.id] = customer.group;
        }
        return accumulator;
      }, {});
      setCustomerGroupMap(map);
    } catch (error) {
      console.error('Error loading customer groups:', error);
    }
  };

  const getStatusText = (statusId: number) => {
    const found = statuses.find(s => s.id === statusId);
    return found ? found.title : (loadingStatuses ? 'Loading...' : 'Unknown');
  };

  const getStatusBadgeLabel = (statusId: number) => {
    const label = getStatusText(statusId);
    return label === 'Unknown' || label === 'Status' ? 'Unknown' : label;
  };

  const getStatusBadgeColor = (statusTitle: string) => {
    const normalized = statusTitle.trim().toLowerCase();

    switch (normalized) {
      case 'in progress':
        return '#4b5563';
      case 'request':
        return '#6b7280';
      case 'completed':
      case 'confirmed':
        return '#16a34a';
      case 'opportunity':
        return '#111111';
      case 'follow-up':
        return '#2563eb';
      case 'maintenance':
      case 'internal':
        return '#334155';
      case 'lost':
      case 'canceled':
      case 'cancelled':
      case 'rejected':
        return '#dc2626';
      case 'support':
        return '#a1a1aa';
      case 'lead':
      case 'unknown':
      default:
        return '#6b7280';
    }
  };

  const tagBadgeColor = '#6b7280';

  const getProjectTagNames = (project: Project): string[] => {
    const projectAny = project as Project;

    const fromProjectTagsArray = (projectAny.projectTags || []).map(tag => (
      tag?.name
    ));

    const tagNames = [...fromProjectTagsArray]
      .filter((name): name is string => Boolean(name && String(name).trim()))
      .map(name => String(name).trim());

    return Array.from(new Set(tagNames));
  };

  return (
    <div>
      <div className="table-responsive">
        <table className="table table-striped align-middle">
          <thead>
            <tr>
              <th className="text-start">Title</th>
              <th className="text-start">Date</th>
              <th className="text-start">Customer</th>
              <th className="text-start">Status</th>
              <th className="text-start">Transactions</th>
              <th className="text-start">Day off</th>
              <th className="text-start">Tags</th>
              <th className="text-start">Actions</th>
            </tr>
          </thead>
        <tbody>
          {projects.map((project) => (
            <tr key={project.id}>
              <td>{project.title}</td>
              <td>{formatDate(project.date)}</td>
              <td>
                <Link
                  to={`/customers/${project.customerId}`}
                  className="text-decoration-none"
                  style={{ color: 'inherit' }}
                >
                  <div className="fw-bold">{project.customerTitle}</div>
                  {customerGroupMap[project.customerId] && (
                    <div className="text-muted" style={{ fontSize: '0.85rem' }}>
                      {customerGroupMap[project.customerId]}
                    </div>
                  )}
                </Link>
              </td>
              <td>
                <span
                  className="badge contact-type-badge"
                  style={{ backgroundColor: getStatusBadgeColor(getStatusText(project.statusId)), color: '#fff' }}
                >
                  {getStatusBadgeLabel(project.statusId)}
                </span>
              </td>
              <td>{project.transactions.length}</td>
              <td>
                {project.isDayOffProject ? (
                  <span className="badge contact-type-badge" style={{ backgroundColor: '#2563eb', color: '#fff' }}>
                    Day off
                  </span>
                ) : (
                  <span className="text-muted">No</span>
                )}
              </td>
              <td>
                {getProjectTagNames(project).length > 0
                  ? (
                    <div className="d-flex flex-wrap gap-1">
                      {getProjectTagNames(project).map(tag => (
                        <span
                          key={`${project.id}-${tag}`}
                          className="badge contact-type-badge"
                          style={{ backgroundColor: tagBadgeColor, color: '#fff' }}
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )
                  : <span className="text-muted">No tags</span>
                }
              </td>
              <td>
                <div className="btn-group action-group" role="group">
                  {onCreateTransaction && (
                    <button
                      className="btn btn-sm action-btn action-edit"
                      onClick={() => onCreateTransaction(project.id)}
                      title="Create Transaction"
                    >
                      <i className="fa-solid fa-file-invoice-dollar" aria-hidden="true" />
                    </button>
                  )}
                  <button
                    className="btn btn-sm action-btn action-view"
                    onClick={() => onViewDetails(project.id)}
                    title="View Details"
                  >
                    <i className="fa-solid fa-eye" aria-hidden="true" />
                  </button>
                  <button
                    className="btn btn-sm action-btn action-edit"
                    onClick={() => onEdit(project)}
                    title="Edit"
                  >
                    <i className="fa-solid fa-pen" aria-hidden="true" />
                  </button>
                  <button
                    className="btn btn-sm action-btn action-delete"
                    onClick={() => onDelete(project.id)}
                    title="Delete"
                  >
                    <i className="fa-solid fa-trash" aria-hidden="true" />
                  </button>
                </div>
              </td>
            </tr>
          ))}
        </tbody>
        </table>
      </div>
    </div>
  );
};

export default ProjectsTable;
